// WP13: a city we don't cover yet is set up on demand by the server's `city` function
// (build/handoff/WP13-city-on-demand-server.md, parts C and D). Every call fails soft: a server without the function
// (404) leaves today's behaviour in place.
import { callFn, notThere, type Plan } from './plan'

export type CityState = 'live' | 'building' | 'new' | 'need_city' | 'unknown' | 'failed' | 'limit'
export type CityReply = { status: CityState; slug?: string; label?: string; places?: number; tz?: string; error?: string }

/** Free text the person typed: at most 80 characters, no control characters (the server caps it too). */
const clean = (s: string) => s.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, 80)

export const resolveCity = (place: string) => callFn<CityReply>('city', { resolve: clean(place) })
export const buildCity = (placeOrSlug: string) => callFn<CityReply>('city', { build: clean(placeOrSlug) })
export const cityStatus = (slug: string) => callFn<CityReply>('city', { status: slug })

/**
 * What the Where step does with a destination outside LA: `need_city` keeps them on the step, `unknown` shows the
 * not-found line, anything else (including a server without the function, or a network error) carries on.
 */
export async function checkPlace(place: string): Promise<'ok' | 'need_city' | 'unknown'> {
  try {
    const r = await resolveCity(place)
    return r.status === 'need_city' ? 'need_city' : r.status === 'unknown' ? 'unknown' : 'ok'
  } catch (e) {
    if (!notThere(e)) console.warn('city resolve failed', e)
    return 'ok'
  }
}

/** A plan whose pool is Los Angeles (no city, or the LA slug). Scout and LA-only lines apply only to these. */
export const isLA = (plan: Plan | null | undefined) => !plan?.dest?.city || plan.dest.city === 'los-angeles'
/** A plan whose city the server is still setting up. */
export const isBuilding = (plan: Plan | null | undefined) => Boolean(plan?.dest?.building && plan.dest.city)
